import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Spinner } from 'react-bootstrap';
import api from '../../../api';
import {
  GetRoadmapResponse,
  RoadmapColumn,
  RoadmapFeature,
  RoadmapAssignee,
} from '@dreamai/types';
import './roadmap-board.scss';

interface EditingFeature {
  id: string;
  field: 'title' | 'description' | 'requestedBy' | 'sizeInDays';
}

interface TeamMember {
  id: string;
  name: string;
  email: string;
}

const FEATURE_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#f43f5e', // Rose
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
];

const SECTION_ORDER = ['Now', 'Next', 'Later', 'Future', 'Done'];

const RoadmapBoard: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [columns, setColumns] = useState<RoadmapColumn[]>([]);
  const [features, setFeatures] = useState<RoadmapFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['Now', 'Next']),
  );
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(
    new Set(),
  );
  const [editing, setEditing] = useState<EditingFeature | null>(null);
  const [editValue, setEditValue] = useState('');
  const [addingFeatureToSection, setAddingFeatureToSection] = useState<
    string | null
  >(null);
  const [newFeature, setNewFeature] = useState({
    title: '',
    description: '',
    requestedBy: '',
  });
  const [addingCardToFeature, setAddingCardToFeature] = useState<string | null>(
    null,
  );
  const [newCardTitle, setNewCardTitle] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [assigningToFeature, setAssigningToFeature] = useState<string | null>(
    null,
  );

  const fetchRoadmap = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetRoadmapResponse>('/roadmap', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setColumns(response.data.columns);
        setFeatures(response.data.features);
      } else {
        setError('Failed to load roadmap');
      }
    } catch (err) {
      console.error('Error fetching roadmap:', err);
      setError(err instanceof Error ? err.message : 'Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<{
        success: boolean;
        members: TeamMember[];
      }>('/team', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setTeamMembers(response.data.members);
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchRoadmap();
    fetchTeamMembers();
  }, [fetchRoadmap, fetchTeamMembers]);

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  const toggleFeature = (featureId: string) => {
    setExpandedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(featureId)) {
        next.delete(featureId);
      } else {
        next.add(featureId);
      }
      return next;
    });
  };

  const getSortedSections = () => {
    return columns.sort((a, b) => {
      const aIndex = SECTION_ORDER.indexOf(a.name);
      const bIndex = SECTION_ORDER.indexOf(b.name);
      if (aIndex === -1 && bIndex === -1) return a.position - b.position;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  };

  const handleAddFeature = async (columnId: string | null) => {
    if (!newFeature.title.trim()) return;

    try {
      const token = await getAccessTokenSilently();
      const color =
        FEATURE_COLORS[Math.floor(Math.random() * FEATURE_COLORS.length)];
      const response = await api.post(
        '/roadmap/features',
        {
          title: newFeature.title.trim(),
          description: newFeature.description.trim() || undefined,
          requestedBy: newFeature.requestedBy.trim() || undefined,
          columnId: columnId || undefined,
          color,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success && response.data.feature) {
        setFeatures((prev) => [...prev, response.data.feature]);
      }
      setNewFeature({ title: '', description: '', requestedBy: '' });
      setAddingFeatureToSection(null);
    } catch (err) {
      console.error('Error adding feature:', err);
    }
  };

  const handleAddCard = async (featureId: string) => {
    if (!newCardTitle.trim()) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/roadmap/cards',
        { featureId, title: newCardTitle.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success && response.data.card) {
        setFeatures((prev) =>
          prev.map((f) =>
            f.id === featureId
              ? { ...f, cards: [...f.cards, response.data.card] }
              : f,
          ),
        );
      }
      setNewCardTitle('');
      setAddingCardToFeature(null);
    } catch (err) {
      console.error('Error adding card:', err);
    }
  };

  const handleUpdateFeature = async (
    featureId: string,
    field: 'title' | 'description' | 'requestedBy' | 'sizeInDays',
    value: string,
  ) => {
    try {
      const token = await getAccessTokenSilently();
      let updateValue: string | number | null;

      if (field === 'sizeInDays') {
        updateValue = value.trim() ? parseFloat(value) : null;
      } else {
        updateValue = value.trim() || null;
      }

      await api.patch(
        `/roadmap/features/${featureId}`,
        { [field]: updateValue },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setFeatures((prev) =>
        prev.map((f) =>
          f.id === featureId ? { ...f, [field]: updateValue } : f,
        ),
      );
    } catch (err) {
      console.error('Error updating feature:', err);
    }
    setEditing(null);
  };

  const handleToggleFeatureDone = async (
    featureId: string,
    isDone: boolean,
  ) => {
    try {
      const token = await getAccessTokenSilently();
      await api.patch(
        `/roadmap/features/${featureId}`,
        { isDone },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setFeatures((prev) =>
        prev.map((f) =>
          f.id === featureId
            ? { ...f, isDone, doneAt: isDone ? new Date().toISOString() : null }
            : f,
        ),
      );
    } catch (err) {
      console.error('Error toggling feature done:', err);
    }
  };

  const handleAssignUser = async (featureId: string, userId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<{
        success: boolean;
        assignee?: RoadmapAssignee;
      }>(
        '/roadmap/assignees',
        { userId, featureId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success && response.data.assignee) {
        setFeatures((prev) =>
          prev.map((f) =>
            f.id === featureId
              ? { ...f, assignees: [...f.assignees, response.data.assignee!] }
              : f,
          ),
        );
      }
      setAssigningToFeature(null);
    } catch (err) {
      console.error('Error assigning user:', err);
    }
  };

  const handleUnassignUser = async (featureId: string, assigneeId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await api.delete(`/roadmap/assignees/${assigneeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setFeatures((prev) =>
        prev.map((f) =>
          f.id === featureId
            ? {
                ...f,
                assignees: f.assignees.filter((a) => a.id !== assigneeId),
              }
            : f,
        ),
      );
    } catch (err) {
      console.error('Error unassigning user:', err);
    }
  };

  const handleToggleCardDone = async (
    featureId: string,
    cardId: string,
    isDone: boolean,
  ) => {
    try {
      const token = await getAccessTokenSilently();
      await api.patch(
        `/roadmap/cards/${cardId}`,
        { isDone },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setFeatures((prev) =>
        prev.map((f) =>
          f.id === featureId
            ? {
                ...f,
                cards: f.cards.map((c) =>
                  c.id === cardId
                    ? {
                        ...c,
                        isDone,
                        doneAt: isDone ? new Date().toISOString() : null,
                      }
                    : c,
                ),
              }
            : f,
        ),
      );
    } catch (err) {
      console.error('Error toggling card done:', err);
    }
  };

  const handleDeleteFeature = async (featureId: string) => {
    if (
      !confirm(
        'Are you sure you want to delete this feature and all its cards?',
      )
    ) {
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      await api.delete(`/roadmap/features/${featureId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setFeatures((prev) => prev.filter((f) => f.id !== featureId));
    } catch (err) {
      console.error('Error deleting feature:', err);
    }
  };

  const handleMoveFeature = async (
    featureId: string,
    targetColumnId: string,
  ) => {
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/roadmap/features/move',
        { featureId, targetColumnId, targetPosition: 0 },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setFeatures((prev) =>
        prev.map((f) =>
          f.id === featureId ? { ...f, columnId: targetColumnId } : f,
        ),
      );
    } catch (err) {
      console.error('Error moving feature:', err);
    }
  };

  const startEditing = (
    id: string,
    field: 'title' | 'description' | 'requestedBy' | 'sizeInDays',
    currentValue: string,
  ) => {
    setEditing({ id, field });
    setEditValue(currentValue || '');
  };

  const getFeaturesForSection = (columnId: string | null) => {
    return features
      .filter((f) => f.columnId === columnId)
      .sort((a, b) => a.position - b.position);
  };

  if (loading) {
    return (
      <div className='roadmap-loading'>
        <Spinner animation='border' size='sm' />
        <p>Loading roadmap...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='roadmap-error'>
        <p>{error}</p>
      </div>
    );
  }

  const renderFeatureCard = (
    feature: RoadmapFeature,
    section: RoadmapColumn,
  ) => {
    const isExpanded = expandedFeatures.has(feature.id);

    return (
      <div
        key={feature.id}
        className={`feature-card ${feature.isDone ? 'done' : ''} ${isExpanded ? 'expanded' : ''}`}
        style={{ borderLeftColor: feature.color || '#6366f1' }}
      >
        <div
          className='feature-header'
          onClick={() => toggleFeature(feature.id)}
        >
          <input
            type='checkbox'
            checked={feature.isDone}
            onChange={(e) => {
              e.stopPropagation();
              handleToggleFeatureDone(feature.id, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
            ▶
          </span>
          {editing?.id === feature.id && editing.field === 'title' ? (
            <div
              className='edit-inline-container'
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type='text'
                className='edit-input'
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')
                    handleUpdateFeature(feature.id, 'title', editValue);
                  if (e.key === 'Escape') setEditing(null);
                }}
                autoFocus
              />
              <button
                className='save-btn'
                onClick={() =>
                  handleUpdateFeature(feature.id, 'title', editValue)
                }
              >
                Save
              </button>
              <button className='cancel-btn' onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          ) : (
            <span
              className='feature-title'
              onDoubleClick={(e) => {
                e.stopPropagation();
                startEditing(feature.id, 'title', feature.title);
              }}
            >
              {feature.title}
            </span>
          )}
          <div className='feature-meta'>
            {feature.cards.length > 0 && (
              <span className='cards-count'>
                {feature.cards.filter((c) => c.isDone).length}/
                {feature.cards.length}
              </span>
            )}
            <select
              className='move-select'
              value={feature.columnId || ''}
              onChange={(e) => {
                e.stopPropagation();
                handleMoveFeature(feature.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {columns.map((col) => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
            <button
              className='delete-btn'
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFeature(feature.id);
              }}
              title='Delete feature'
            >
              ×
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className='feature-details'>
            <div className='detail-row'>
              <label>Description</label>
              {editing?.id === feature.id && editing.field === 'description' ? (
                <div className='edit-block-container'>
                  <textarea
                    className='edit-textarea'
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setEditing(null);
                    }}
                    autoFocus
                    rows={3}
                  />
                  <div className='edit-actions'>
                    <button
                      className='save-btn'
                      onClick={() =>
                        handleUpdateFeature(
                          feature.id,
                          'description',
                          editValue,
                        )
                      }
                    >
                      Save
                    </button>
                    <button
                      className='cancel-btn'
                      onClick={() => setEditing(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  className='description-text'
                  onClick={() =>
                    startEditing(
                      feature.id,
                      'description',
                      feature.description || '',
                    )
                  }
                >
                  {feature.description || 'Click to add description...'}
                </p>
              )}
            </div>

            <div className='detail-row'>
              <label>Requested By</label>
              {editing?.id === feature.id && editing.field === 'requestedBy' ? (
                <div className='edit-block-container'>
                  <input
                    type='text'
                    className='edit-input'
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter')
                        handleUpdateFeature(
                          feature.id,
                          'requestedBy',
                          editValue,
                        );
                      if (e.key === 'Escape') setEditing(null);
                    }}
                    placeholder='e.g. Acme Corp, John Smith'
                    autoFocus
                  />
                  <div className='edit-actions'>
                    <button
                      className='save-btn'
                      onClick={() =>
                        handleUpdateFeature(
                          feature.id,
                          'requestedBy',
                          editValue,
                        )
                      }
                    >
                      Save
                    </button>
                    <button
                      className='cancel-btn'
                      onClick={() => setEditing(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  className='requested-by-text'
                  onClick={() =>
                    startEditing(
                      feature.id,
                      'requestedBy',
                      feature.requestedBy || '',
                    )
                  }
                >
                  {feature.requestedBy || 'Click to add customers...'}
                </p>
              )}
            </div>

            <div className='detail-row'>
              <label>Size Estimate (days)</label>
              {editing?.id === feature.id && editing.field === 'sizeInDays' ? (
                <div className='edit-block-container'>
                  <input
                    type='number'
                    step='0.5'
                    min='0'
                    className='edit-input size-input'
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter')
                        handleUpdateFeature(
                          feature.id,
                          'sizeInDays',
                          editValue,
                        );
                      if (e.key === 'Escape') setEditing(null);
                    }}
                    placeholder='e.g. 2.5'
                    autoFocus
                  />
                  <div className='edit-actions'>
                    <button
                      className='save-btn'
                      onClick={() =>
                        handleUpdateFeature(feature.id, 'sizeInDays', editValue)
                      }
                    >
                      Save
                    </button>
                    <button
                      className='cancel-btn'
                      onClick={() => setEditing(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  className='size-text'
                  onClick={() =>
                    startEditing(
                      feature.id,
                      'sizeInDays',
                      feature.sizeInDays?.toString() || '',
                    )
                  }
                >
                  {feature.sizeInDays != null
                    ? `${feature.sizeInDays} days`
                    : 'Click to add estimate...'}
                </p>
              )}
            </div>

            <div className='detail-row'>
              <label>Assignees</label>
              <div className='assignees-section'>
                <div className='assignees'>
                  {feature.assignees.map((a) => (
                    <span key={a.id} className='assignee-badge'>
                      {a.userName || a.userId.slice(0, 8)}
                      <button
                        className='remove-assignee'
                        onClick={() => handleUnassignUser(feature.id, a.id)}
                        title='Remove assignee'
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {assigningToFeature === feature.id ? (
                  <div className='assign-dropdown'>
                    <select
                      autoFocus
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAssignUser(feature.id, e.target.value);
                        }
                      }}
                      onBlur={() => setAssigningToFeature(null)}
                    >
                      <option value=''>Select a team member...</option>
                      {teamMembers
                        .filter(
                          (m) =>
                            !feature.assignees.some((a) => a.userId === m.id),
                        )
                        .map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.name || member.email}
                          </option>
                        ))}
                    </select>
                    <button
                      className='cancel-btn'
                      onClick={() => setAssigningToFeature(null)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    className='assign-btn'
                    onClick={() => setAssigningToFeature(feature.id)}
                  >
                    + Assign
                  </button>
                )}
              </div>
            </div>

            <div className='cards-section'>
              <label>
                Tasks ({feature.cards.filter((c) => c.isDone).length}/
                {feature.cards.length})
              </label>
              <div className='cards-list'>
                {feature.cards.map((card) => (
                  <div
                    key={card.id}
                    className={`card-item ${card.isDone ? 'done' : ''}`}
                  >
                    <input
                      type='checkbox'
                      checked={card.isDone}
                      onChange={(e) =>
                        handleToggleCardDone(
                          feature.id,
                          card.id,
                          e.target.checked,
                        )
                      }
                    />
                    <span className='card-title'>{card.title}</span>
                  </div>
                ))}

                {addingCardToFeature === feature.id ? (
                  <div className='add-card-form'>
                    <input
                      type='text'
                      placeholder='Task title...'
                      value={newCardTitle}
                      onChange={(e) => setNewCardTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddCard(feature.id);
                        if (e.key === 'Escape') {
                          setAddingCardToFeature(null);
                          setNewCardTitle('');
                        }
                      }}
                      autoFocus
                    />
                    <button onClick={() => handleAddCard(feature.id)}>
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setAddingCardToFeature(null);
                        setNewCardTitle('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    className='add-card-btn'
                    onClick={() => setAddingCardToFeature(feature.id)}
                  >
                    + Add task
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='roadmap-board'>
      <div className='roadmap-header'>
        <h2>Product Roadmap</h2>
        <span className='feature-count'>{features.length} features</span>
      </div>

      <div className='roadmap-sections'>
        {getSortedSections().map((section) => {
          const sectionFeatures = getFeaturesForSection(section.id);
          const isExpanded = expandedSections.has(section.name);

          return (
            <div
              key={section.id}
              className={`roadmap-section ${section.isDoneColumn ? 'done-section' : ''} ${isExpanded ? 'expanded' : ''}`}
            >
              <div
                className='section-header'
                onClick={() => toggleSection(section.name)}
              >
                <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                  ▶
                </span>
                <h3>{section.name}</h3>
                <span className='section-count'>{sectionFeatures.length}</span>
              </div>

              {isExpanded && (
                <div className='section-content'>
                  {sectionFeatures.map((feature) =>
                    renderFeatureCard(feature, section),
                  )}

                  {addingFeatureToSection === section.id ? (
                    <div className='add-feature-form'>
                      <input
                        type='text'
                        placeholder='Feature title...'
                        value={newFeature.title}
                        onChange={(e) =>
                          setNewFeature({
                            ...newFeature,
                            title: e.target.value,
                          })
                        }
                        autoFocus
                      />
                      <textarea
                        placeholder='Description (optional)...'
                        value={newFeature.description}
                        onChange={(e) =>
                          setNewFeature({
                            ...newFeature,
                            description: e.target.value,
                          })
                        }
                        rows={2}
                      />
                      <input
                        type='text'
                        placeholder='Requested by (optional)...'
                        value={newFeature.requestedBy}
                        onChange={(e) =>
                          setNewFeature({
                            ...newFeature,
                            requestedBy: e.target.value,
                          })
                        }
                      />
                      <div className='form-actions'>
                        <button onClick={() => handleAddFeature(section.id)}>
                          Add Feature
                        </button>
                        <button
                          onClick={() => {
                            setAddingFeatureToSection(null);
                            setNewFeature({
                              title: '',
                              description: '',
                              requestedBy: '',
                            });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className='add-feature-btn'
                      onClick={() => setAddingFeatureToSection(section.id)}
                    >
                      + Add feature
                    </button>
                  )}

                  {/* Section Assignment Overview */}
                  {(() => {
                    const userTotals = new Map<
                      string,
                      { name: string; totalDays: number; featureCount: number }
                    >();

                    sectionFeatures.forEach((feature) => {
                      const days = feature.sizeInDays ?? 0;
                      feature.assignees.forEach((assignee) => {
                        const existing = userTotals.get(assignee.userId);
                        if (existing) {
                          existing.totalDays += days;
                          existing.featureCount += 1;
                        } else {
                          userTotals.set(assignee.userId, {
                            name:
                              assignee.userName || assignee.userId.slice(0, 8),
                            totalDays: days,
                            featureCount: 1,
                          });
                        }
                      });
                    });

                    if (userTotals.size === 0) return null;

                    return (
                      <div className='section-assignment-overview'>
                        <div className='user-totals'>
                          {Array.from(userTotals.entries()).map(
                            ([uId, data]) => (
                              <div key={uId} className='user-total-row'>
                                <span className='user-name'>{data.name}</span>
                                <span className='user-stats'>
                                  {data.featureCount} feature
                                  {data.featureCount !== 1 ? 's' : ''} •{' '}
                                  <strong>{data.totalDays} days</strong>
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RoadmapBoard;
